import contactApi from "@/services/api/contact";
import { useAuthStore } from "@/stores/useAuthStore";
import { App as AntApp, Button, Form, Grid, Input } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Footer from "../layout/Footer";
import PrivacyPolicyModal from "./PrivacyPolicyModal";

// 添加自定义样式
const customStyles = `
  .new-contact-us-green {
    color: #2BBDB2 !important;
  }
  .new-contact-us-button {
    background-color: #2BBDB2 !important;
    border-color: #2BBDB2 !important;
    color: #ffffff !important;
  }
  .new-contact-us-button:hover {
    background-color: #2BBDB2 !important;
    border-color: #2BBDB2 !important;
  }
  .ant-input:focus,
  .ant-input-focused {
    border-color: #2BBDB2 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2) !important;
  }
  .ant-input:hover {
    border-color: #2BBDB2 !important;
  }
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused {
    border-color: #2BBDB2 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2) !important;
  }
  .ant-input-affix-wrapper:hover {
    border-color: #2BBDB2 !important;
  }
  .ant-input-textarea:focus,
  .ant-input-textarea-focused {
    border-color: #2BBDB2 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2) !important;
  }
  .ant-input-textarea:hover {
    border-color: #2BBDB2 !important;
  }
`;

interface NewContactUsProps {
  onSubmitSuccess?: () => void;
}

// const SECTION_CARD_STYLE: React.CSSProperties = {
//   background: '#ffffff',
//   border: '1px solid #e5e7eb',
//   borderRadius: 8,
//   boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
// }

const NewContactUs: React.FC<NewContactUsProps> = ({ onSubmitSuccess }) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { message } = AntApp.useApp();
  const { token } = useAuthStore();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);

  // 注入自定义样式
  React.useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // 在组件加载时发送事件，通知 Footer 组件页面已打开
  useEffect(() => {
    // 设置数据属性，便于 Footer 检测
    document.body.setAttribute("data-contact-us-page", "open");

    // 发送全局事件
    window.dispatchEvent(new CustomEvent("contactUsOpened"));

    // 组件卸载时发送关闭事件
    return () => {
      document.body.removeAttribute("data-contact-us-page");
      window.dispatchEvent(new CustomEvent("contactUsClosed"));
    };
  }, []);

  const handleSubmit = async () => {
    // 提交申请时验证登录状态
    if (!token) {
      message.warning(t("beta_apply_form.tip_login_first"));
      // 触发全局登录事件，同时保存来源为contact
      localStorage.setItem("loginSource", "contact");
      window.dispatchEvent(new CustomEvent("globalSelectLogin"));
      return;
    }

    try {
      setLoading(true);
      const values = await form.validateFields();
      // 构造提交数据，字段映射
      const submitData = {
        name: values.name,
        wechat_id: values.wechat_id,
        company_name: values.company_name,
        brand: values.brand, // 新增监测品牌字段
        brand_industry: values.brand_industry, // 使用品牌所属行业/品类作为industry字段
        business: values.business || "",
        email: values.email,
        phone: values.phone,
        im: values.im,
        channel: values.channel,
        website_url: values.website_url,
      };

      // 调用真实的内测申请API
      await contactApi.submit(submitData);
      // 更新审核状态
      localStorage.setItem("audit_status", "0");
      message.success(t("beta_apply_form.success_submit"));

      onSubmitSuccess?.();
      form.resetFields();
    } catch (error: any) {
      console.error("表单提交失败:", error);
      // 显示具体的错误信息
      if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.warning(t("beta_apply_form.error_incomplete"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        paddingTop: "20px",
        position: "relative",
      }}
      data-contact-us-page
    >
      {/* 顶部 Hero */}
      <header
        style={{
          padding: "0px 12px",
          background: "#FFFFFF",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div
            style={{
              color: "#21C4B8",
              //fontSize: isMobile ? 28 : 36,
              fontSize: isMobile ? 28 : 52,
              fontWeight: 600,
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            {t("beta_apply_page.hero_title")}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              marginBottom: 5,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                color: "#333333",
                fontSize: isMobile ? 18 : 22,
                fontWeight: 400,
                lineHeight: 1.5,
                marginBottom: 20,
              }}
            >
              {t("beta_apply_page.hero_desc")}
            </div>
          </div>
          <Button
            //type="primary"
            //size={isMobile ? 'large' : 'large'}
            className="new-contact-us-button"
            onClick={() => {
              const el = document.getElementById("new-contact-form");
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{
              borderRadius: 25,
              padding: "12px 32px",
              fontSize: 18,
              fontWeight: 500,
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
          >
            {t("beta_apply_components.btn_apply")}
          </Button>
        </div>
      </header>

      {/* 主体内容 */}
      <main style={{ flex: 1 }}>
        <section style={{ padding: isMobile ? "20px 0" : "36px 24px" }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            {/* 加入理由 */}
            <div
              style={{
                background: "#FFFFFF",
                padding: "32px 12px",
                borderRadius: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 28,
                  color: "#333333",
                  fontWeight: 700,
                  margin: "0 0 24px 0",
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                {t("beta_apply_page.section_why_join")}
              </h2>
              <div
                style={{
                  color: "#333333",
                  fontSize: 18,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textIndent: "2em",
                }}
              >
                {t("beta_apply_page.long_text_ai_era")}
              </div>
              <div
                style={{
                  color: "#333333",
                  fontSize: 18,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  textIndent: "2em",
                  marginTop: "5px",
                }}
              >
                {t("beta_apply_page.long_text_ai_era1")}
              </div>
            </div>

            {/* 内测联盟权益 */}
          </div>
        </section>

        {/* 内测联盟权益 - 扩展到屏幕宽度 */}
        <section
          style={{
            background: "rgba(34, 190, 181, 0.14)",
            padding: isMobile ? "64px 12px" : "64px 32px",
            width: "100%",
          }}
        >
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <h2
              style={{
                fontSize: isMobile ? 28 : 28,
                color: "#333333",
                fontWeight: 700,
                textAlign: "center",
                margin: "0 0 40px 0",
                lineHeight: 1.3,
              }}
            >
              {t("beta_apply_page.section_benefits_list")}
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: 24,
                width: "100%",
              }}
            >
              {/* 权益卡片1 */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 12,
                  padding: "24px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "#ecfdf5",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2BBDB2",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  1
                </div>
                <h3
                  style={{
                    fontSize: 20,
                    color: "#333333",
                    fontWeight: 700,
                    margin: "24px 0 16px 0",
                    lineHeight: 1.3,
                  }}
                >
                  {t("beta_apply_page.benefit_7day_title")}
                </h3>
                <p
                  style={{
                    fontSize: 16,
                    color: "#6B7280",
                    fontWeight: 400,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {t("beta_apply_page.section_benefits_step1")}
                </p>
              </div>

              {/* 权益卡片2 */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 12,
                  padding: "24px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "#ecfdf5",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2BBDB2",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  2
                </div>
                <h3
                  style={{
                    fontSize: 20,
                    color: "#333333",
                    fontWeight: 700,
                    margin: "24px 0 16px 0",
                    lineHeight: 1.3,
                  }}
                >
                  {t("beta_apply_page.benefit_priority")}
                </h3>
                <p
                  style={{
                    fontSize: 16,
                    color: "#6B7280",
                    fontWeight: 400,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {t("beta_apply_page.section_benefits_step2")}
                </p>
              </div>

              {/* 权益卡片3 */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 12,
                  padding: "24px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "#ecfdf5",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2BBDB2",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  3
                </div>
                <h3
                  style={{
                    fontSize: 20,
                    color: "#333333",
                    fontWeight: 700,
                    margin: "24px 0 16px 0",
                    lineHeight: 1.3,
                  }}
                >
                  {t("beta_apply_page.benefit_report_title")}
                </h3>
                <p
                  style={{
                    fontSize: 16,
                    color: "#6B7280",
                    fontWeight: 400,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {t("beta_apply_page.section_benefits_step3")}
                </p>
              </div>

              {/* 权益卡片4 */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 12,
                  padding: "24px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: "#ecfdf5",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2BBDB2",
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  4
                </div>
                <h3
                  style={{
                    fontSize: 20,
                    color: "#333333",
                    fontWeight: 700,
                    margin: "24px 0 16px 0",
                    lineHeight: 1.3,
                  }}
                >
                  {t("beta_apply_page.benefit_member_title")}
                </h3>
                <p
                  style={{
                    fontSize: 16,
                    color: "#6B7280",
                    fontWeight: 400,
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {t("beta_apply_page.section_benefits_step4")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section style={{ padding: isMobile ? "20px 12px" : "36px 24px" }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            {/* 参与步骤 */}
            <div
              style={{
                background: "#ffffff",
                padding: isMobile ? "0" : "0 40px",
                borderRadius: 12,
                marginBottom: 32,
              }}
            >
              <h2
                style={{
                  fontSize: 30,
                  color: "#333333",
                  fontWeight: 700,
                  textAlign: "center",
                  margin: "0 0 16px 0",
                  lineHeight: 1.3,
                }}
              >
                {t("beta_apply_page.section_how_to")}
              </h2>
              <div
                style={{
                  color: "#666666",
                  fontSize: 18,
                  fontWeight: 400,
                  textAlign: "center",
                  margin: "0 0 48px 0",
                  lineHeight: 1.5,
                }}
              >
                {t("beta_apply_page.sub_title")}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 32,
                  maxWidth: isMobile ? 800 : 600,
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                {/* 步骤1 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 20,
                    flexDirection: "row",
                    textAlign: "left",
                    marginRight: isMobile ? 0 : 40,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      background: "#2BBDB2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: 18,
                      fontWeight: 700,
                      flexShrink: 0,
                      marginRight: 8,
                      marginTop: 10,
                    }}
                  >
                    1
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: 20,
                        color: "#333333",
                        fontWeight: 700,
                        margin: "0 0 8px 0",
                        lineHeight: 1.3,
                      }}
                    >
                      {t("beta_apply_page.step_1_title")}
                    </h3>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#666666",
                        fontWeight: 400,
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {t("beta_apply_page.step_1_desc")}
                    </p>
                  </div>
                </div>

                {/* 步骤2 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 20,
                    flexDirection: "row",
                    textAlign: "left",
                    marginRight: isMobile ? 0 : 40,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      background: "#2BBDB2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: 18,
                      fontWeight: 700,
                      flexShrink: 0,
                      marginRight: 8,
                      marginTop: 10,
                    }}
                  >
                    2
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: 20,
                        color: "#333333",
                        fontWeight: 700,
                        margin: "0 0 8px 0",
                        lineHeight: 1.3,
                      }}
                    >
                      {t("beta_apply_page.step_2_title")}
                    </h3>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#666666",
                        fontWeight: 400,
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {t("beta_apply_page.step_2_desc")}
                    </p>
                  </div>
                </div>

                {/* 步骤3 */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 20,
                    flexDirection: "row",
                    textAlign: "left",
                    marginRight: isMobile ? 0 : 40,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      background: "#2BBDB2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: 18,
                      fontWeight: 700,
                      flexShrink: 0,
                      marginRight: 8,
                      marginTop: 10,
                    }}
                  >
                    3
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: 20,
                        color: "#333333",
                        fontWeight: 700,
                        margin: "0 0 8px 0",
                        lineHeight: 1.3,
                      }}
                    >
                      {t("beta_apply_page.step_3_title")}
                    </h3>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#666666",
                        fontWeight: 400,
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {t("beta_apply_page.step_3_desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 常见问题 */}
            <div
              style={{
                background: "#ffffff",
                padding: "32px 0px",
                borderRadius: 12,
              }}
            >
              <h2
                style={{
                  fontSize: 30,
                  color: "#333333",
                  fontWeight: 700,
                  textAlign: "center",
                  margin: "0 0 48px 0",
                  lineHeight: 1.3,
                }}
              >
                {t("beta_apply_page.section_faq")}
              </h2>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                  width: "100%",
                  maxWidth: 768,
                  margin: "0 auto",
                }}
              >
                {/* 问题1 */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 8,
                    padding: "24px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h3
                    style={{
                      fontSize: 18,
                      color: "#333333",
                      fontWeight: 700,
                      margin: "0 0 12px 0",
                      lineHeight: 1.4,
                    }}
                  >
                    Q：{t("beta_apply_page.faq_q1")}
                  </h3>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#666666",
                      fontWeight: 400,
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    A：{t("beta_apply_page.faq_a1")}
                  </p>
                </div>

                {/* 问题2 */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 8,
                    padding: "24px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h3
                    style={{
                      fontSize: 18,
                      color: "#333333",
                      fontWeight: 700,
                      margin: "0 0 12px 0",
                      lineHeight: 1.4,
                    }}
                  >
                    Q：{t("beta_apply_page.faq_q2")}
                  </h3>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#666666",
                      fontWeight: 400,
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    A：{t("beta_apply_page.faq_a2")}
                  </p>
                </div>

                {/* 问题3 */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 8,
                    padding: "24px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h3
                    style={{
                      fontSize: 18,
                      color: "#333333",
                      fontWeight: 700,
                      margin: "0 0 12px 0",
                      lineHeight: 1.4,
                    }}
                  >
                    Q：{t("beta_apply_page.faq_q3")}
                  </h3>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#666666",
                      fontWeight: 400,
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    A：{t("beta_apply_page.faq_a3")}
                  </p>
                </div>

                {/* 问题4 */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 8,
                    padding: "24px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h3
                    style={{
                      fontSize: 18,
                      color: "#333333",
                      fontWeight: 700,
                      margin: "0 0 12px 0",
                      lineHeight: 1.4,
                    }}
                  >
                    Q：{t("beta_apply_page.faq_q4")}
                  </h3>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#666666",
                      fontWeight: 400,
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    A：{t("beta_apply_page.faq_a4")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 加入我们 */}
        <section
          style={{
            background: "#2BBDB2",
            padding: "32px 12px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: "100%", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: isMobile ? 22 : 30,
                color: "#ffffff",
                fontWeight: 700,
                margin: "0 0 24px 0",
                lineHeight: 1.3,
              }}
            >
              {t("beta_apply_page.cta_join_title")}
            </h2>
            <p
              style={{
                fontSize: 18,
                color: "#ffffff",
                fontWeight: 400,
                margin: "0 0 40px 0",
                lineHeight: 1.5,
              }}
            >
              {t("beta_apply_page.cta_join_desc")}
            </p>
            <Button
              //type="primary"
              //size="large"
              //className="new-contact-us-button"
              onClick={() => {
                const el = document.getElementById("new-contact-form");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              style={{
                background: "#ffffff",
                borderColor: "#ffffff",
                color: "#2BBDB2",
                borderRadius: 25,
                padding: "12px 32px",
                fontSize: 16,
                fontWeight: 600,
                height: "auto",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
            >
              {t("beta_apply_components.btn_apply")}
            </Button>
          </div>
        </section>

        {/* 表单 */}
        <section
          id="new-contact-form"
          style={{
            background: "#ffffff",
            paddingTop: "32px",
          }}
        >
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <h2
              style={{
                fontSize: isMobile ? 22 : 30,
                color: "#333333",
                fontWeight: 700,
                textAlign: "center",
                margin: "0 0 16px 0",
                lineHeight: 1.3,
              }}
            >
              {t("beta_apply_page.form_header_title")}
            </h2>
            <p
              style={{
                fontSize: 18,
                color: "#666666",
                fontWeight: 400,
                textAlign: "center",
                margin: "0 12px 10px 12px",
                lineHeight: 1.5,
              }}
            >
              {t("beta_apply_page.form_intro_desc")}
            </p>

            <div
              style={{
                background: "#ffffff",
                padding: "32px 20px 0 20px",
              }}
            >
              <Form layout="vertical" form={form} requiredMark={false}>
                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_name")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_name"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_name")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_monitor_brand")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="brand"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_brand_name"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_brand_name")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_company")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="company_name"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_company"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_company")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_email")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_email"),
                    },
                    {
                      type: "email",
                      message: t("beta_apply_form.validate_email"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_email")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_phone")}
                    </span>
                  }
                  name="phone"
                  rules={[
                    {
                      required: false,
                      message: t("beta_apply_form.placeholder_phone"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_phone")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_im")}
                    </span>
                  }
                  name="im"
                  rules={[
                    {
                      required: false,
                      message: t("beta_apply_form.placeholder_im"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_im")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_website_url")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="website_url"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_website_url"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_website_url")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                {/* <Form.Item
                  label={<span style={{ color: '#333333', fontWeight: 500 }}>{t('beta_apply_form.label_wechat')}<span style={{ color: '#ff4d4f' }}>*</span></span>}
                  name="wechat_id"
                  rules={[{ required: true, message: t('beta_apply_form.placeholder_wechat') }]}
                >
                  <Input placeholder={t('beta_apply_form.placeholder_wechat')} allowClear style={{ height: 48, fontSize: 16, borderRadius: 60 }} />
                </Form.Item> */}

                {/* <Form.Item
                  label={<span style={{ color: '#333333', fontWeight: 500 }}>{t('beta_apply_form.label_industry')}<span style={{ color: '#ff4d4f' }}>*</span></span>}
                  name="brand_industry"
                  rules={[{ required: true, message: t('beta_apply_form.placeholder_industry') }]}
                >
                  <Input placeholder={t('beta_apply_form.placeholder_industry')} allowClear style={{ height: 48, fontSize: 16, borderRadius: 60 }} />
                </Form.Item> */}

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_business")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="business"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_business"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("beta_apply_form.placeholder_business")}
                    allowClear
                    style={{ height: 48, fontSize: 16, borderRadius: 60 }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ color: "#333333", fontWeight: 500 }}>
                      {t("beta_apply_form.label_channel")}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                  }
                  name="channel"
                  rules={[
                    {
                      required: true,
                      message: t("beta_apply_form.placeholder_channel_error"),
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder={t("beta_apply_form.placeholder_channel")}
                    rows={3}
                    style={{ fontSize: 16, borderRadius: 20 }}
                  />
                </Form.Item>

                {/* <Form.Item
                  label={<span style={{ color: '#333333', fontWeight: 500 }}>{t('beta_apply_form.label_position')}<span style={{ color: '#ff4d4f' }}>*</span></span>}
                  name="position"
                  rules={[{ required: true, message: t('beta_apply_form.placeholder_position') }]}
                >
                  <Input placeholder={t('beta_apply_form.placeholder_position')} allowClear style={{ height: 48, fontSize: 16, borderRadius: 60 }} />
                </Form.Item> */}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: 32,
                  }}
                >
                  <Button
                    //type="primary"
                    //size="large"
                    loading={loading}
                    onClick={handleSubmit}
                    style={{
                      background: "#2BBDB2",
                      borderColor: "#2BBDB2",
                      borderRadius: 60,
                      padding: "12px 48px",
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#ffffff",
                      height: 48,
                      minWidth: 200,
                    }}
                  >
                    {loading
                      ? t("beta_apply_form.status_submitting")
                      : t("beta_apply_form.btn_submit")}
                  </Button>
                  <div
                    style={{
                      textAlign: "left",
                      marginTop: 32,
                      fontSize: 14,
                      color: "#666666",
                      lineHeight: 1.4,
                    }}
                  >
                    <span style={{ color: "#ff4d4f" }}>*</span>
                    {t("beta_apply_form.click_agree_prefix")}
                    <span
                      style={{
                        color: "#1890ff",
                        textDecoration: "underline",
                        cursor: "pointer",
                        padding: "0 5px",
                      }}
                      onClick={() => setPrivacyPolicyOpen(true)}
                    >
                      {t("beta_apply_form.link_privacy")}
                    </span>
                    {t("beta_apply_form.processing_info")}
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </section>
      </main>
      {/* 隐私政策弹窗 */}
      <PrivacyPolicyModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
      />
      <Footer />
    </div>
  );
};

export default NewContactUs;
